// RITA-1498 (Nutzungserfassung Phase 1b) — Puffer und Versandauslöser (Konzept 4.2).
//
// Ereignisse werden im Speicher gesammelt und alle FLUSH_INTERVAL_MS, ab FLUSH_AT Ereignissen
// oder auf Anforderung (Tab wird verlassen) gesendet — in Bündeln zu höchstens MAX_PER_BATCH
// (Servergrenze MAX_EVENTS_PER_BATCH). Ein Fehlversuch wird genau einmal wiederholt, dann
// verworfen. Meldet der Server "Schalter aus" oder "nicht angemeldet", stoppt der Puffer
// dauerhaft. Nichts hier darf werfen oder die Bedienung blockieren.
import type { IUsageEvent } from 'global-intern-types';

export type SendOutcome = 'ok' | 'retry' | 'drop' | 'stop' | 'session_conflict';
export type FlushReason = 'interval' | 'size' | 'hide' | 'manual' | 'retry';

export const FLUSH_INTERVAL_MS = 10_000;
export const FLUSH_AT = 50;
export const MAX_PER_BATCH = 100;
export const MAX_BUFFERED = 500;
export const RETRY_DELAY_MS = 2_000;

export interface BatchQueueDeps {
  send: (events: IUsageEvent[], reason: FlushReason) => Promise<SendOutcome>;
  flushIntervalMs?: number;
  flushAt?: number;
  maxPerBatch?: number;
  maxBuffered?: number;
  retryDelayMs?: number;
  onStop?: () => void;
  onSessionConflict?: () => void;
}

export interface BatchQueue {
  push(event: IUsageEvent): void;
  flush(reason: FlushReason): Promise<void>;
  stop(): void;
  size(): number;
  peek(): readonly IUsageEvent[];
  isStopped(): boolean;
}

export const createBatchQueue = ({
  send,
  flushIntervalMs = FLUSH_INTERVAL_MS,
  flushAt = FLUSH_AT,
  maxPerBatch = MAX_PER_BATCH,
  maxBuffered = MAX_BUFFERED,
  retryDelayMs = RETRY_DELAY_MS,
  onStop,
  onSessionConflict,
}: BatchQueueDeps): BatchQueue => {
  let buffer: IUsageEvent[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const clearTimer = (): void => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const halt = (): void => {
    stopped = true;
    buffer = [];
    clearTimer();
  };

  const attempt = async (events: IUsageEvent[], reason: FlushReason): Promise<SendOutcome> => {
    try {
      return await send(events, reason);
    } catch {
      return 'retry';
    }
  };

  const dispatch = async (events: IUsageEvent[], reason: FlushReason, isRetry: boolean): Promise<void> => {
    if (stopped) return;
    const outcome = await attempt(events, reason);
    if (stopped) return;
    if (outcome === 'stop') {
      halt();
      if (onStop) onStop();
      return;
    }
    if (outcome === 'session_conflict') {
      if (onSessionConflict) onSessionConflict();
      return;
    }
    if (outcome === 'retry' && !isRetry) {
      setTimeout(() => {
        dispatch(events, 'retry', true).catch(() => undefined);
      }, retryDelayMs);
    }
  };

  const flush = async (reason: FlushReason): Promise<void> => {
    clearTimer();
    if (stopped || buffer.length === 0) return;
    const chunks: IUsageEvent[][] = [];
    while (buffer.length > 0) {
      chunks.push(buffer.splice(0, maxPerBatch));
    }
    await Promise.all(chunks.map((chunk) => dispatch(chunk, reason, false)));
  };

  return {
    push(event: IUsageEvent): void {
      if (stopped) return;
      buffer.push(event);
      if (buffer.length > maxBuffered) {
        buffer.splice(0, buffer.length - maxBuffered);
      }
      if (buffer.length >= flushAt) {
        flush('size').catch(() => undefined);
        return;
      }
      if (timer === null) {
        timer = setTimeout(() => {
          flush('interval').catch(() => undefined);
        }, flushIntervalMs);
      }
    },
    flush,
    stop(): void {
      halt();
    },
    size: () => buffer.length,
    peek: () => buffer,
    isStopped: () => stopped,
  };
};
